/**
 * KAN-670: диалог карточки продавца Telegram-аккаунтов: создать или изменить
 * (имя, страница покупки, рейтинг звёздами, заметка). После сохранения
 * вызывает onSaved с карточкой из ответа API.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RatingStars } from '@/components/telegram/RatingStars';
import { useTelegramSellerMutations } from '@/hooks/useTelegramSellers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { validateSellerUrl } from '@/lib/sellerLink';
import type { TelegramSeller } from '@/types/telegramSeller';

export const SELLER_NAME_MAX = 100;
export const SELLER_PAGE_URL_MAX = 300;
export const SELLER_NOTES_MAX = 2000;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Карточка для изменения; нет: создание. */
  seller?: TelegramSeller | null;
  onSaved?: (seller: TelegramSeller) => void;
}

export function SellerDialog({ open, onOpenChange, seller, onSaved }: Props): JSX.Element {
  const { create, update } = useTelegramSellerMutations();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(seller?.name ?? '');
    setUrl(seller?.url ?? '');
    setRating(seller?.rating ?? null);
    setNotes(seller?.notes ?? '');
    setError(null);
  }, [open, seller]);

  const pending = create.isPending || update.isPending;
  const editing = !!seller;

  const submit = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Укажите имя продавца');
      return;
    }
    const urlProblem = validateSellerUrl(url);
    if (urlProblem) {
      setError(urlProblem);
      return;
    }
    setError(null);
    const body = { name: trimmed, url: url.trim(), rating, notes: notes.trim() };
    try {
      const saved = editing
        ? await update.mutateAsync({ id: seller.id, data: body })
        : await create.mutateAsync(body);
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      setError(extractErrorMessage(err, 'Не удалось сохранить продавца'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="seller-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Продавец: изменить' : 'Новый продавец'}</DialogTitle>
          <DialogDescription>
            Продавец аккаунтов: имя, страница покупки и ваша оценка. Выбирается при
            подключении и импорте аккаунта.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="seller-dialog-name">Имя</Label>
            <Input
              id="seller-dialog-name"
              data-testid="seller-dialog-name"
              value={name}
              maxLength={SELLER_NAME_MAX}
              autoComplete="off"
              placeholder="Например, Tg_Hub"
              disabled={pending}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="seller-dialog-url">Страница покупки</Label>
            <Input
              id="seller-dialog-url"
              data-testid="seller-dialog-url"
              value={url}
              maxLength={SELLER_PAGE_URL_MAX}
              autoComplete="off"
              placeholder="https://..."
              disabled={pending}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Рейтинг</Label>
            <RatingStars
              value={rating}
              onChange={setRating}
              disabled={pending}
              size="md"
              testId="seller-dialog-rating"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="seller-dialog-notes">Заметка</Label>
            <Textarea
              id="seller-dialog-notes"
              data-testid="seller-dialog-notes"
              rows={3}
              maxLength={SELLER_NOTES_MAX}
              value={notes}
              disabled={pending}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {error && (
            <p className="text-xs text-red-700" data-testid="seller-dialog-error">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Отмена
          </Button>
          <Button data-testid="seller-dialog-submit" disabled={pending} onClick={submit}>
            {pending ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
